import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard,
  Mail,
  Building2, 
  Users,
  LogOut, 
  User as UserIcon,
  Menu,
  X,
  Briefcase,
  FolderKanban,
  Truck
} from 'lucide-react';
import { Button } from 'react-bootstrap';
import { useUser } from '../contexts/UserContext';
import './Dashboard.css';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, logout, canAccessUserManagement } = useUser();

  useEffect(() => {
    // Force Poppins font on all elements in this component
    const style = document.createElement('style');
    style.textContent = `
      .layout-wrapper *,
      .layout-wrapper *::before,
      .layout-wrapper *::after {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Get company ID for user/companyAdmin roles
  const getCompanyId = () => {
    if (user?.company?.id) return user.company.id;
    if (user?.assetId) return user.assetId;
    return null;
  };

  // Define all possible menu items
  const allMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', requiresRole: ['superAdmin'] },
    {
      id: 'companies',
      label: (user?.role === 'user' || user?.role === 'companyAdmin') ? 'Company' : 'Companies Management',
      icon: Building2,
      path: (user?.role === 'user' || user?.role === 'companyAdmin') && getCompanyId() 
        ? `/company/${getCompanyId()}/projects` 
        : '/companies'
    },
    { id: 'users', label: 'Users', icon: Users, path: '/users', requiresRole: ['superAdmin', 'companyAdmin'] },
    { id: 'email', label: 'Email', icon: Mail, path: '/email', requiresRole: ['superAdmin'] },
    { id: 'applications', label: 'Applications', icon: Briefcase, path: '/applications', requiresRole: ['superAdmin'] },
    { id: 'deliveries', label: 'Deliveries', icon: Truck, path: '/deliveries', requiresRole: ['superAdmin'] },
    { id: 'portfolio-projects', label: 'Projects', icon: FolderKanban, path: '/portfolio-projects', requiresRole: ['superAdmin'] },
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(item => {
    if (item.requiresRole) {
      if (loading || !user) return false;
      return item.requiresRole.includes(user.role);
    }
    return true;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'dashboard';
    if (path === '/applications') return 'applications';
    if (path === '/deliveries') return 'deliveries';
    if (path === '/portfolio-projects') return 'portfolio-projects';
    // Check if path matches company projects pattern for user/companyAdmin
    if ((user?.role === 'user' || user?.role === 'companyAdmin') && path.match(/^\/company\/\d+\/projects/)) {
      return 'companies';
    }
    const menuItem = menuItems.find(item => path.startsWith(item.path));
    return menuItem ? menuItem.id : 'companies';
  };

  const getPageTitle = () => {
    const activeTab = getActiveTab();
    const menuItem = menuItems.find(item => item.id === activeTab);
    if (menuItem) return menuItem.label;
    if (user?.role === 'user' || user?.role === 'companyAdmin') return 'Company';
    return 'Companies Management';
  };

  return (
    <div className="layout-wrapper">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <img src="/Logo.jpg" alt="Tyler Reinforcing" className="logo-image" />
          </div>
          <button 
            className="sidebar-close d-md-none"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>
        
        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = getActiveTab() === item.id;
            return (
              <button
                key={item.id}
                className={`nav-item${isActive ? ' active' : ''}`}
                onClick={() => handleNavigation(item.path)}
              >
                <Icon />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <hr className="sidebar-divider" />
          <div className="sidebar-bottom">
            <button className="profile-link" onClick={() => navigate('/profile')}>
              <UserIcon />
              <span>Profile</span>
            </button>
            <button className="signout-link" onClick={handleLogout}>
              <LogOut />
              <span>Sign Out</span>
            </button>
            <div className="sidebar-appname">Tyler Reinforcing</div>
            <div className="sidebar-version">v1.0.0</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Navigation - Hide completely for regular users */}
        {user?.role !== 'user' && (
          <div className="top-nav">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Button
                  variant="light"
                  className="sidebar-toggle d-md-none me-3"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu size={20} />
                </Button>
                <div className="page-title">
                  {getPageTitle()}
                </div>
              </div>
              <div className="top-nav-actions">
                {/* Add any top navigation actions here */}
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <main className="dashboard-main">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className={`mobile-overlay ${sidebarOpen ? 'active' : ''}`} onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default Layout; 